/**
 * [INPUT]: 依赖 ./types 的 Landmark
 * [OUTPUT]: 对外提供 SEM 语义点索引表、at(landmarks, key) 访问器、pupils/midline 等派生点、derollLandmarks 去滚转
 * [POS]: apps/web/src/scorer 的关键点语义层，把 MediaPipe 468 裸索引映射为解剖语义点，被 geo-features 消费
 * [PROTOCOL]: 变更时更新此头部，然后检查 ../CLAUDE.md
 *
 * 约定：MediaPipe 图像坐标系，x 向右、y 向下；"左/右"以图像为准（图像左 = 被试者右脸）。
 * 索引取自 MediaPipe FaceLandmarker 官方 468 标定；trichion/gonion 为近似代理点，靠参数校准吸收误差。
 */
import type { Landmark } from './types'
import { midpoint, distance, rotateAround } from './geometry'

/** 语义点 → MediaPipe 468 索引 */
export const SEM = {
  trichion: 10, // 额顶（发际近似代理）
  nasion: 6, // 鼻根（眉间下、双眼中线）
  subnasale: 2, // 鼻底
  gnathion: 152, // 颏下点（下巴底）
  noseTip: 4, // 鼻尖
  noseBridge: 168, // 鼻梁中点（对称轴参考）

  // 左眼（图像左侧）
  leftEyeOuter: 33, // 外眦
  leftEyeInner: 133, // 内眦
  leftEyeTop: 159,
  leftEyeBottom: 145,
  // 右眼（图像右侧）
  rightEyeInner: 362, // 内眦
  rightEyeOuter: 263, // 外眦
  rightEyeTop: 386,
  rightEyeBottom: 374,

  leftCheek: 234, // 左颧/面界（近似 bizygomatic 左）
  rightCheek: 454, // 右颧/面界
  leftGonion: 172, // 左下颌角近似
  rightGonion: 397, // 右下颌角近似
  leftJaw: 58, // 左下颌缘
  rightJaw: 288, // 右下颌缘

  leftMouth: 61, // 左口角
  rightMouth: 291, // 右口角
  upperLip: 13, // 上唇中
  lowerLip: 14, // 下唇中
} as const

export type SemKey = keyof typeof SEM

/** 安全访问：索引越界返回 (0,0) 点并允许上层判定。 */
export function at(landmarks: Landmark[], key: SemKey): Landmark {
  const idx = SEM[key]
  return landmarks[idx] ?? { x: 0, y: 0 }
}

/** 左右瞳孔代理点（用内外眦中点近似；iris 模型未启用时用此）。 */
export function pupils(landmarks: Landmark[]): { left: Landmark; right: Landmark } {
  return {
    left: midpoint(at(landmarks, 'leftEyeOuter'), at(landmarks, 'leftEyeInner')),
    right: midpoint(at(landmarks, 'rightEyeOuter'), at(landmarks, 'rightEyeInner')),
  }
}

/** 瞳距（尺度归一化基准）。 */
export function pupilDistance(landmarks: Landmark[]): number {
  const { left, right } = pupils(landmarks)
  return distance(left, right)
}

/** 面部竖直对称轴 x（用内眦中点与鼻梁中点的均值，较稳健）。 */
export function midlineX(landmarks: Landmark[]): number {
  const innerMid = midpoint(at(landmarks, 'leftEyeInner'), at(landmarks, 'rightEyeInner'))
  const bridge = at(landmarks, 'noseBridge')
  return (innerMid.x + bridge.x) / 2
}

/**
 * 去滚转：把关键点绕瞳孔中点旋转 -rollDeg，使双眼连线水平。
 * 消除滚转 roll 对 canthal_tilt（≈ 真值 + roll，roll 直接叠加进眦角）与 symmetry_index
 * （mirrorX 只翻 x 不翻 y，roll 会污染左右配对）的污染——这是同脸多照评分漂移的主因。
 * 仅旋转 x/y；z 原样保留（2D 指标不读 z）。rollDeg≈0 时原样返回（恒等，合成正脸夹具不受影响）。
 */
export function derollLandmarks(landmarks: Landmark[], rollDeg: number): Landmark[] {
  if (Math.abs(rollDeg) < 1e-6) return landmarks
  const { left, right } = pupils(landmarks)
  const center = midpoint(left, right)
  return landmarks.map((p) => {
    const r = rotateAround(p, -rollDeg, center)
    return { x: r.x, y: r.y, z: p.z, visibility: p.visibility }
  })
}